import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from '@/components/ui/sidebar';
import { useSidebar } from '@/components/ui/sidebar-context';
import type { NavGroup } from '@/config/navigation';
import { useNavGroupActive } from './utils/nav.utils';
import { UpcomingBadge } from './UpcomingBadge';
import {
  SIDEBAR_BUTTON_CLASS,
  SIDEBAR_SUB_BUTTON_CLASS,
  DISABLED_BUTTON_CLASS,
  CHEVRON_TRANSITION_CLASS,
  CHEVRON_OPEN_CLASS,
  COLLAPSIBLE_CONTENT_CLASS,
  MENU_LABEL_CLASS,
  SUBMENU_CONTAINER_CLASS,
  SUBMENU_ACTIVE_INDICATOR_CLASS,
} from './sidebar.constants';

interface CollapsibleNavGroupProps {
  group: NavGroup;
  forceOpen?: boolean;
}

export function CollapsibleNavGroup({ group, forceOpen = false }: CollapsibleNavGroupProps) {
  const { state } = useSidebar();
  const { isChildActive, isActive } = useNavGroupActive(group);
  const shouldBeOpen = forceOpen || isActive;
  const [manualOpen, setManualOpen] = useState<boolean | null>(null);
  const [syncedShouldBeOpen, setSyncedShouldBeOpen] = useState(shouldBeOpen);

  if (shouldBeOpen !== syncedShouldBeOpen) {
    setSyncedShouldBeOpen(shouldBeOpen);
    setManualOpen(null);
  }

  const open = manualOpen ?? shouldBeOpen;

  const Icon = group.icon;
  const isCollapsed = state === 'collapsed';
  const firstNavigableChild = group.children.find((c) => !c.upcoming);

  const collapsedButton = firstNavigableChild ? (
    <SidebarMenuButton
      isActive={isActive}
      tooltip={group.label}
      render={<Link to={firstNavigableChild.href as '/'} />}
      className={SIDEBAR_BUTTON_CLASS}
    >
      <Icon />
    </SidebarMenuButton>
  ) : (
    <SidebarMenuButton
      tooltip={group.label}
      aria-disabled="true"
      tabIndex={-1}
      className={cn(SIDEBAR_BUTTON_CLASS, DISABLED_BUTTON_CLASS)}
    >
      <Icon />
    </SidebarMenuButton>
  );

  return (
    <Collapsible open={open} onOpenChange={setManualOpen}>
      <SidebarMenuItem>
        {isCollapsed ? (
          collapsedButton
        ) : (
          <CollapsibleTrigger
            render={
              <SidebarMenuButton
                isActive={isActive}
                tooltip={group.label}
                className={SIDEBAR_BUTTON_CLASS}
              >
                <Icon />
                <span className={MENU_LABEL_CLASS}>{group.label}</span>
                <ChevronDown
                  className={cn(
                    'ml-auto size-4 text-muted-foreground/60 shrink-0',
                    CHEVRON_TRANSITION_CLASS,
                    open && CHEVRON_OPEN_CLASS,
                  )}
                />
              </SidebarMenuButton>
            }
          />
        )}
        <CollapsibleContent className={COLLAPSIBLE_CONTENT_CLASS}>
          <SidebarMenuSub className={SUBMENU_CONTAINER_CLASS}>
            {group.children.map((child) => {
              const childActive = isChildActive(child);
              return (
                <SidebarMenuSubItem
                  key={child.href}
                  className={childActive ? SUBMENU_ACTIVE_INDICATOR_CLASS : undefined}
                >
                  {child.upcoming ? (
                    <SidebarMenuSubButton
                      aria-disabled="true"
                      tabIndex={-1}
                      className={cn(SIDEBAR_SUB_BUTTON_CLASS, DISABLED_BUTTON_CLASS)}
                    >
                      <span className="flex-1 truncate">{child.label}</span>
                      <UpcomingBadge />
                    </SidebarMenuSubButton>
                  ) : (
                    <SidebarMenuSubButton
                      isActive={childActive}
                      render={<Link to={child.href as '/'} />}
                      className={SIDEBAR_SUB_BUTTON_CLASS}
                    >
                      <span className="flex-1 truncate">{child.label}</span>
                    </SidebarMenuSubButton>
                  )}
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
